import { useState, useMemo, useRef } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { cn } from '@/lib/utils'
import {
  FileText, Plus, Pencil, Trash2, RefreshCw, Users, ExternalLink, Eye, EyeOff,
  Film, ChevronDown, ChevronUp, DollarSign, CheckCircle, XCircle, StickyNote, Download,
  BarChart3, History, ClipboardList, Tag, X, TrendingUp, TrendingDown, Camera,
} from 'lucide-react'
import { VideoAnalytics } from './VideoAnalytics'
import { PublishHistory } from './PublishHistory'
import { ContentUsage } from './ContentUsage'
import { FollowerOverview } from './FollowerOverview'
import { FollowerCandleChart } from './FollowerCandleChart'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'
import {
  useFinancePages, useCreatePage, useUpdatePage, useDeletePage, useSyncVideos, usePageVideos,
  useImportFromAccounts, useSavePageTags, useChangePagePhoto, type FinancePage,
} from '@/api/hooks/usePages'

const fmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtNum = new Intl.NumberFormat('pt-BR')

function PageDialog({
  open, onOpenChange, editPage,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  editPage: FinancePage | null
}) {
  const { t } = useLanguage()
  const [name, setName] = useState(editPage?.name ?? '')
  const [link, setLink] = useState(editPage?.link ?? '')
  const [fbPageId, setFbPageId] = useState(editPage?.fbPageId ?? '')
  const [accessToken, setAccessToken] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [isMonetized, setIsMonetized] = useState(editPage?.isMonetized ?? false)
  const [followerCount, setFollowerCount] = useState(editPage?.followerCount?.toString() ?? '0')
  const [responsible, setResponsible] = useState(editPage?.responsible ?? '')
  const [notes, setNotes] = useState(editPage?.notes ?? '')

  const createMut = useCreatePage()
  const updateMut = useUpdatePage()
  const saving = createMut.isPending || updateMut.isPending

  const handleOpenChange = (v: boolean) => {
    if (v) {
      setName(editPage?.name ?? '')
      setLink(editPage?.link ?? '')
      setFbPageId(editPage?.fbPageId ?? '')
      setAccessToken('')
      setShowToken(false)
      setIsMonetized(editPage?.isMonetized ?? false)
      setFollowerCount(editPage?.followerCount?.toString() ?? '0')
      setResponsible(editPage?.responsible ?? '')
      setNotes(editPage?.notes ?? '')
    }
    onOpenChange(v)
  }

  const handleSave = () => {
    if (!name.trim()) { toast.error(t('Informe o nome da pagina')); return }

    const data: Record<string, any> = {
      name: name.trim(),
      link: link.trim() || undefined,
      fbPageId: fbPageId.trim() || undefined,
      isMonetized,
      followerCount: parseInt(followerCount) || 0,
      responsible: responsible.trim(),
      notes: notes.trim(),
    }

    if (accessToken.trim()) {
      data.accessToken = accessToken.trim()
    }

    if (editPage) {
      updateMut.mutate({ id: editPage.id, ...data }, {
        onSuccess: () => { toast.success(t('Pagina atualizada!')); handleOpenChange(false) },
        onError: (e) => toast.error(e.message),
      })
    } else {
      createMut.mutate(data, {
        onSuccess: () => { toast.success(t('Pagina criada!')); handleOpenChange(false) },
        onError: (e) => toast.error(e.message),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editPage ? t('Editar Pagina') : t('Nova Pagina')}</DialogTitle>
          <DialogDescription>{t('Cadastre uma pagina do Facebook para acompanhamento.')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>{t('Nome da pagina')}</Label>
            <Input className="mt-1" placeholder={t('Ex: Reality Shows Brasil')} value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <Label>{t('Link da pagina')}</Label>
            <Input className="mt-1" placeholder="https://www.facebook.com/NomeDaPagina" value={link} onChange={(e) => setLink(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Facebook Page ID</Label>
              <Input className="mt-1" placeholder="Ex: 123456789" value={fbPageId} onChange={(e) => setFbPageId(e.target.value)} />
            </div>
            <div>
              <Label>{t('Seguidores')}</Label>
              <Input type="number" min={0} className="mt-1" value={followerCount} onChange={(e) => setFollowerCount(e.target.value)} />
            </div>
          </div>

          <div>
            <Label>{t('Access Token (pra sincronizar videos)')}</Label>
            <div className="relative mt-1">
              <Input
                type={showToken ? 'text' : 'password'}
                placeholder={editPage?.hasToken ? t('Token ja salvo (deixe vazio pra manter)') : t('Cole o token da pagina...')}
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>{t('Monetizada')}</Label>
              <p className="text-xs text-muted-foreground">{t('Esta pagina gera receita de anuncios?')}</p>
            </div>
            <Switch checked={isMonetized} onCheckedChange={setIsMonetized} />
          </div>

          <div>
            <Label>{t('Responsavel')}</Label>
            <Input className="mt-1" placeholder={t('Nome da pessoa responsavel')} value={responsible} onChange={(e) => setResponsible(e.target.value)} />
          </div>

          <div>
            <Label>{t('Observacoes')}</Label>
            <Textarea className="mt-1" rows={3} placeholder={t('Anotacoes sobre a pagina...')} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>{t('Cancelar')}</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('Salvando...') : t('Salvar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function VideoSection({ pageId, hasToken }: { pageId: string; hasToken: boolean }) {
  const { t } = useLanguage()
  const { data: videos = [], isLoading } = usePageVideos(pageId)
  const syncMut = useSyncVideos()

  const handleSync = () => {
    if (!hasToken) { toast.error(t('Adicione um access token primeiro')); return }
    syncMut.mutate(pageId, {
      onSuccess: (data: any) => toast.success(`${data.synced} ${t('videos sincronizados!')}`),
      onError: (e) => toast.error(e.message),
    })
  }

  return (
    <div className="mt-3 pt-3 border-t">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold">{t('Videos')}</span>
        <Button
          variant="outline" size="sm" className="h-6 text-xs gap-1"
          onClick={handleSync}
          disabled={syncMut.isPending}
        >
          <RefreshCw className={`h-3 w-3 ${syncMut.isPending ? 'animate-spin' : ''}`} />
          {syncMut.isPending ? t('Sincronizando...') : t('Sincronizar')}
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-16 rounded" />
      ) : videos.length === 0 ? (
        <div className="text-xs text-muted-foreground text-center py-3">
          {hasToken ? t('Nenhum video. Clique em Sincronizar.') : t('Adicione um access token pra sincronizar.')}
        </div>
      ) : (
        <div className="max-h-[200px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">{t('Titulo')}</TableHead>
                <TableHead className="text-xs text-right">{t('Views')}</TableHead>
                <TableHead className="text-xs w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {videos.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="text-xs max-w-[200px] truncate">{v.title || t('Sem titulo')}</TableCell>
                  <TableCell className="text-xs text-right">{fmtNum.format(v.views)}</TableCell>
                  <TableCell>
                    {v.url && (
                      <a href={v.url.startsWith('/') ? `https://www.facebook.com${v.url}` : v.url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function PagePic({ page, cacheBust }: { page: FinancePage; cacheBust?: number }) {
  const [error, setError] = useState(false)
  const initial = (page.name || '?').charAt(0).toUpperCase()

  if (error || !page.fbPageId) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-blue-500/20 text-sm font-bold">
        {initial}
      </div>
    )
  }

  const src = `/api/v2/page-picture/${page.fbPageId}${cacheBust ? `?t=${cacheBust}` : ''}`

  return (
    <img
      src={src}
      alt={page.name}
      className="h-10 w-10 shrink-0 rounded-xl border-2 border-border object-cover"
      onError={() => setError(true)}
    />
  )
}

function PageCard({
  page, onEdit, onDelete,
}: {
  page: FinancePage
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useLanguage()
  const [expanded, setExpanded] = useState(false)
  const [editingTags, setEditingTags] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const saveTags = useSavePageTags()
  const changePhotoMut = useChangePagePhoto()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [picCacheBust, setPicCacheBust] = useState(0)

  const handleChangePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!page.hasToken || !page.fbPageId) {
      toast.error(t('Pagina precisa de Page ID e Access Token'))
      return
    }
    changePhotoMut.mutate({ id: page.id, file }, {
      onSuccess: () => {
        toast.success(t('Foto da pagina alterada!'))
        setPicCacheBust(Date.now())
      },
      onError: (err) => toast.error(err.message),
    })
    e.target.value = ''
  }

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase()
    if (!tag) return
    const current = page.tags || []
    if (current.includes(tag)) { setTagInput(''); return }
    saveTags.mutate({ id: page.id, tags: [...current, tag] })
    setTagInput('')
  }

  const handleRemoveTag = (tag: string) => {
    saveTags.mutate({ id: page.id, tags: (page.tags || []).filter(t => t !== tag) })
  }

  return (
    <Card className="p-4 hover:border-primary/30 transition-colors">
      <div className="flex items-start gap-3">
        <PagePic page={page} cacheBust={picCacheBust} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm truncate">{page.name}</h3>
            <Badge variant={page.isMonetized ? 'default' : 'secondary'} className="text-xs shrink-0">
              {page.isMonetized ? <><CheckCircle className="h-3 w-3 mr-1" /> {t('Monetizada')}</> : <><XCircle className="h-3 w-3 mr-1" /> {t('Nao monetizada')}</>}
            </Badge>
          </div>
          {page.link && (
            <a href={page.link} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary truncate block mt-0.5">
              {page.link}
            </a>
          )}
        </div>
      </div>

      {/* Tags */}
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {(page.tags || []).map(tag => (
          <span key={tag} className="inline-flex items-center gap-0.5 rounded bg-muted px-1.5 py-0.5 text-[10px] group">
            #{tag}
            <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" onClick={() => handleRemoveTag(tag)}>
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        {editingTags ? (
          <form onSubmit={e => { e.preventDefault(); handleAddTag() }} className="inline-flex">
            <input
              autoFocus
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onBlur={() => { if (!tagInput.trim()) setEditingTags(false) }}
              placeholder="tag"
              className="h-5 w-16 rounded border border-border/40 bg-transparent px-1 text-[10px] outline-none focus:border-primary/50"
              onKeyDown={e => { if (e.key === 'Escape') { setEditingTags(false); setTagInput('') } }}
            />
          </form>
        ) : (
          <button onClick={() => setEditingTags(true)} className="rounded bg-muted/50 px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            +
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Users className="h-3 w-3" /> {fmtNum.format(page.followerCount)}
          {page.followerDelta !== null && page.followerDelta !== 0 && (
            <span className={page.followerDelta > 0 ? 'text-emerald-500' : 'text-red-500'}>
              ({page.followerDelta > 0 ? '+' : ''}{fmtNum.format(page.followerDelta)})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Film className="h-3 w-3" /> {page.videoCount} {t('videos')}
        </div>
        {page.responsible && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3 w-3" /> {page.responsible}
          </div>
        )}
        <div className={`flex items-center gap-1.5 text-xs font-medium ${page.balance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
          <DollarSign className="h-3 w-3" /> {fmt.format(page.balance)}
        </div>
      </div>

      {page.notes && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
          <StickyNote className="h-3 w-3 mt-0.5 shrink-0" />
          <span className="line-clamp-2">{page.notes}</span>
        </div>
      )}

      <div className="flex gap-2 mt-3 pt-3 border-t">
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={onEdit}>
          <Pencil className="h-3 w-3" /> {t('Editar')}
        </Button>
        <Button
          variant="outline" size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => fileInputRef.current?.click()}
          disabled={changePhotoMut.isPending || !page.hasToken || !page.fbPageId}
          title={t('Trocar foto de perfil da pagina')}
        >
          <Camera className={`h-3 w-3 ${changePhotoMut.isPending ? 'animate-pulse' : ''}`} />
          {changePhotoMut.isPending ? '...' : t('Foto')}
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleChangePhoto} />
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => setExpanded(!expanded)}>
          <Film className="h-3 w-3" /> {t('Videos')}
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-destructive hover:text-destructive" onClick={onDelete}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {expanded && <VideoSection pageId={page.id} hasToken={page.hasToken} />}
    </Card>
  )
}

export function Pages() {
  const { t } = useLanguage()
  const { data: pages = [], isLoading } = useFinancePages()
  const deleteMut = useDeletePage()
  const importMut = useImportFromAccounts()

  const [activeView, setActiveView] = useState<'pages' | 'analytics' | 'history' | 'content' | 'followers'>('pages')
  const [chartPageId, setChartPageId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editPage, setEditPage] = useState<FinancePage | null>(null)
  const [tagFilter, setTagFilter] = useState<string[]>([])

  const allTags = useMemo(() => {
    const tags = new Set<string>()
    pages.forEach(p => (p.tags || []).forEach(t => tags.add(t)))
    return Array.from(tags).sort()
  }, [pages])

  const filteredPages = useMemo(() => {
    if (tagFilter.length === 0) return pages
    return pages.filter(p => tagFilter.some(t => (p.tags || []).includes(t)))
  }, [pages, tagFilter])

  const grouped = useMemo(() => {
    const groups = new Map<string, FinancePage[]>()
    for (const page of filteredPages) {
      const tag = (page.tags && page.tags.length > 0) ? page.tags[0] : '__sem_tag__'
      if (!groups.has(tag)) groups.set(tag, [])
      groups.get(tag)!.push(page)
    }
    return [...groups.entries()].sort((a, b) => {
      if (a[0] === '__sem_tag__') return 1
      if (b[0] === '__sem_tag__') return -1
      return a[0].localeCompare(b[0])
    })
  }, [filteredPages])

  const toggleTag = (tag: string) => {
    setTagFilter(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])
  }

  const openCreate = () => { setEditPage(null); setDialogOpen(true) }
  const openEdit = (page: FinancePage) => { setEditPage(page); setDialogOpen(true) }

  const handleImport = () => {
    importMut.mutate(undefined, {
      onSuccess: (data: any) => {
        const parts = []
        if (data.imported > 0) parts.push(`${data.imported} ${t('importada(s)')}`)
        if (data.updated > 0) parts.push(`${data.updated} ${t('atualizada(s)')}`)
        if (data.removed > 0) parts.push(`${data.removed} ${t('removida(s)')}`)
        if (parts.length > 0) {
          toast.success(`${t('Paginas')}: ${parts.join(', ')}!`)
        } else {
          toast.info(t('Nenhuma conta conectada encontrada'))
        }
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const handleDelete = (page: FinancePage) => {
    if (!confirm(`${t('Deletar pagina')} "${page.name}"? ${t('Os lancamentos financeiros serao mantidos.')}`)) return
    deleteMut.mutate(page.id, {
      onSuccess: () => toast.success(t('Pagina deletada')),
      onError: (e) => toast.error(e.message),
    })
  }

  // Summary stats
  const totalPages = pages.length
  const monetizedCount = pages.filter(p => p.isMonetized).length
  const totalFollowers = pages.reduce((sum, p) => sum + p.followerCount, 0)
  const totalFollowerDelta = pages.reduce((sum, p) => sum + (p.followerDelta ?? 0), 0)
  const pagesWithDelta = pages.filter(p => p.followerDelta !== null).length
  const totalBalance = pages.reduce((sum, p) => sum + p.balance, 0)

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{t('Gerenciamento de Paginas')}</h1>
            <p className="text-sm text-muted-foreground">{t('Acompanhe suas paginas do Facebook')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 rounded-lg bg-muted p-0.5">
            <button
              type="button"
              onClick={() => { setActiveView('pages'); setChartPageId(null) }}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                activeView === 'pages' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <FileText className="h-3.5 w-3.5" /> {t('Paginas')}
            </button>
            <button
              type="button"
              onClick={() => { setActiveView('analytics'); setChartPageId(null) }}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                activeView === 'analytics' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <BarChart3 className="h-3.5 w-3.5" /> {t('Analytics')}
            </button>
            <button
              type="button"
              onClick={() => { setActiveView('content'); setChartPageId(null) }}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                activeView === 'content' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <ClipboardList className="h-3.5 w-3.5" /> {t('Conteudo')}
            </button>
            <button
              type="button"
              onClick={() => setActiveView('followers')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                activeView === 'followers' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Users className="h-3.5 w-3.5" /> {t('Seguidores')}
            </button>
            <button
              type="button"
              onClick={() => { setActiveView('history'); setChartPageId(null) }}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                activeView === 'history' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <History className="h-3.5 w-3.5" /> {t('Historico')}
            </button>
          </div>
          {activeView === 'pages' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleImport} disabled={importMut.isPending}>
                <Download className="h-4 w-4" />
                {importMut.isPending ? t('Importando...') : t('Importar das Contas')}
              </Button>
              <Button size="sm" className="gap-1.5" onClick={openCreate}>
                <Plus className="h-4 w-4" /> {t('Nova Pagina')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {activeView === 'history' ? (
        <PublishHistory />
      ) : activeView === 'content' ? (
        <ContentUsage />
      ) : activeView === 'analytics' ? (
        <VideoAnalytics pages={pages} />
      ) : activeView === 'followers' ? (
        chartPageId
          ? <FollowerCandleChart pages={pages} initialPageId={chartPageId} onBack={() => setChartPageId(null)} />
          : <FollowerOverview pages={pages} onSelectChart={setChartPageId} />
      ) : (
        <>
          {/* Tag Filter */}
          {allTags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <Tag className="h-3.5 w-3.5 text-muted-foreground" />
              {allTags.map(tag => (
                <Badge
                  key={tag}
                  variant={tagFilter.includes(tag) ? 'default' : 'outline'}
                  className="cursor-pointer rounded-lg text-xs transition-all duration-200"
                  onClick={() => toggleTag(tag)}
                >
                  #{tag}
                </Badge>
              ))}
              {tagFilter.length > 0 && (
                <button onClick={() => setTagFilter([])} className="text-[10px] text-muted-foreground hover:text-foreground ml-1">
                  {t('Limpar')}
                </button>
              )}
            </div>
          )}

          {/* Filtered count */}
          {tagFilter.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {filteredPages.length} {t('de')} {pages.length} {t(pages.length !== 1 ? 'paginas' : 'pagina')}
            </p>
          )}

          {/* Summary Stats */}
          {!isLoading && pages.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-4">
              <Card className="p-3 flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <div>
                  <div className="text-xs text-muted-foreground">{t('Total')}</div>
                  <div className="text-lg font-bold">{totalPages}</div>
                </div>
              </Card>
              <Card className="p-3 flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <div>
                  <div className="text-xs text-muted-foreground">{t('Monetizadas')}</div>
                  <div className="text-lg font-bold">{monetizedCount}</div>
                </div>
              </Card>
              <Card className="p-3 flex items-center gap-3">
                <Users className="h-5 w-5 text-blue-500" />
                <div>
                  <div className="text-xs text-muted-foreground">{t('Seguidores')}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">{fmtNum.format(totalFollowers)}</span>
                    {pagesWithDelta > 0 && totalFollowerDelta !== 0 && (
                      <span className={cn(
                        'flex items-center gap-0.5 text-xs font-medium rounded-full px-1.5 py-0.5',
                        totalFollowerDelta > 0
                          ? 'text-emerald-600 bg-emerald-500/10'
                          : 'text-red-500 bg-red-500/10'
                      )}>
                        {totalFollowerDelta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {totalFollowerDelta > 0 ? '+' : ''}{fmtNum.format(totalFollowerDelta)}
                      </span>
                    )}
                  </div>
                  {pagesWithDelta > 0 && pagesWithDelta < pages.length && (
                    <div className="text-[10px] text-amber-500 mt-0.5">
                      {pagesWithDelta}/{pages.length} {t('paginas com dados de ontem')}
                    </div>
                  )}
                </div>
              </Card>
              <Card className="p-3 flex items-center gap-3">
                <DollarSign className={`h-5 w-5 ${totalBalance >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                <div>
                  <div className="text-xs text-muted-foreground">{t('Balanco Total')}</div>
                  <div className="text-lg font-bold">{fmt.format(totalBalance)}</div>
                </div>
              </Card>
            </div>
          )}

          {/* Pages Grid */}
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-4 animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-full mb-3" />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-4 bg-muted rounded" />
                    <div className="h-4 bg-muted rounded" />
                  </div>
                </Card>
              ))}
            </div>
          ) : pages.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="flex justify-center mb-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
                  <FileText className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>
              <h2 className="text-lg font-semibold mb-1">{t('Nenhuma pagina')}</h2>
              <p className="text-sm text-muted-foreground mb-4">
                {t('Cadastre suas paginas do Facebook para acompanhar monetizacao, seguidores e balanco.')}
              </p>
              <Button onClick={openCreate} className="gap-1.5">
                <Plus className="h-4 w-4" /> {t('Nova Pagina')}
              </Button>
            </Card>
          ) : filteredPages.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {t('Nenhuma pagina com essas tags')}
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map(([tag, groupPages]) => {
                const showHeader = grouped.length > 1 || tag !== '__sem_tag__'
                return (
                  <div key={tag}>
                    {showHeader && (
                      <div className="flex items-center gap-2 mb-3">
                        <h2 className="text-sm font-semibold text-muted-foreground">
                          {tag === '__sem_tag__' ? t('Sem tag') : `#${tag}`}
                        </h2>
                        <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
                          {groupPages.length}
                        </Badge>
                        <div className="flex-1 border-t border-border/50" />
                      </div>
                    )}
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {groupPages.map((page) => (
                        <PageCard
                          key={page.id}
                          page={page}
                          onEdit={() => openEdit(page)}
                          onDelete={() => handleDelete(page)}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Dialog */}
          {dialogOpen && (
            <PageDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              editPage={editPage}
            />
          )}

        </>
      )}
    </div>
  )
}
